import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import config from '../../../config'
import type {
    AgenteClaveDTO,
    AseguradoraClaveResponse,
    CalcularRecibosRequest,
    ClienteDTO,
    Conducto,
    CreateClientRequest,
    CreatePolizaAseguradoRequest,
    CreatePolizaFromCaratulaRequest,
    CreatePolizaRequest,
    CreatePolizaVehiculoRequest,
    FormaPago,
    Genero,
    KeyValue,
    LocalDate,
    ModelAutoResponse,
    MonedaDTO,
    PagaebleResult,
    Parentesco,
    PolizaAseguradoDTO,
    PolizaDTO,
    PolizaVehiculoDTO,
    ReciboDTO,
    ReciboStatus,
    StorageFile,
    VendedorDTO,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import type { CustomerFormState } from '../../customers/form/customer-form-model'
import APIInvoke, { LONG_TIMEOUT_MS } from '../../../utils/APIInvoke'
import { PARENTESCOS } from '../../../utils/catalogs'
import { currentYear, formatSpokenDate, formatWithAge, parseDate, toDate, todayIso, toIsoDate, yearsSince } from '../../../utils/dates'
import { clip, onlySimple } from '../../../utils/inputs'
import { formatAmount as formatNumber, sanitizeDecimal } from '../../../utils/numbers'
import { calcularRFC } from '../../../utils/RFCGenerator'
import { errorMessage, FORMA_PAGO, formatPct, NEW_POLICY_PATH, personName, POLICIES_PATH, policyPath } from '../common/policy-common'
import type { PolicyFormLocationState, PolicyReadLocationState } from '../common/policy-common'

export type RamoKey = 'vida' | 'autos' | 'salud' | 'danos'

export interface RamoMeta {
    label: string
    tone: string
    conceptoPh: string
    planPh: string
    /** `RamoDTO.id` que espera `CreatePolizaRequest.ramo`. */
    api: number
    /** Modelo que espera `POST /recibos/calcular`. */
    type: string
}

export type { FormaPagoOption } from '../common/policy-common'
export { formatPct } from '../common/policy-common'

export type GeneroUi = Genero | ''

/** El primero es el titular. `id` es el de `PolizaAseguradoDTO`; solo se reenvía al editar (`PUT`). */
export interface AseguradoForm {
    key: number
    id: number | null
    parentesco: Parentesco | ''
    nombres: string
    apPat: string
    apMat: string
    fecNacimiento: string
    genero: GeneroUi
    rfc: string
}

export type AseguradoField = Exclude<keyof AseguradoForm, 'key' | 'id'>

export type VehiculoDescripcion = Pick<ModelAutoResponse, 'descripcion' | 'marca' | 'tipo' | 'amis'>

/** Opción del autocompletar: resultados del catálogo o el texto libre que se va escribiendo. */
export type VehiculoOption = VehiculoDescripcion & { icon?: string | null; libre?: boolean }

export interface VehiculoForm {
    id: number | null
    modelo: number
    descripcion: VehiculoDescripcion | null
    noSerie: string
    placas: string
}

export type Contratante = Pick<ClienteDTO, 'nombres' | 'apPat' | 'apMat' | 'rfc' | 'sexo' | 'fiscal' | 'edoCivil'>
    & Partial<Pick<ClienteDTO, 'fecNacimiento' | 'esRobot'>>
    & {
        /** `null` si viene de la carátula y aún no existe; al guardar el backend lo busca por RFC o lo da de alta. */
        id: number | null
        /** Datos completos de la carátula (dirección, nacimiento…) para dar de alta al cliente nuevo. */
        nuevo?: CreateClientRequest | null
    }

export type ReciboField = 'primaNeta' | 'derecho' | 'financiamiento' | 'iva'

export type ReciboDateField = 'fecInicial' | 'fecFinal' | 'vencimiento' | 'fecPago'

export interface ReciboForm {
    id?: number | null
    serie: number | null
    totalSerie?: number | null
    primaNeta: number
    derecho: number
    financiamiento: number
    iva: number
    primaTotal: number
    fecInicial: LocalDate | null
    fecFinal: LocalDate | null
    vencimiento: LocalDate | null
    status: ReciboStatus
    folioPago: string
    fecPago: LocalDate | null
    edited?: boolean
}

export interface GeneralesForm {
    contratante: Contratante | null
    aseguradora: number | ''
    agente: number | ''
    moneda: number | ''
    formaPago: FormaPago
    desde: string
    hasta: string
    numero: string
    concepto: string
    plan: string
    vendedor: number | ''
    vendedorEntity: VendedorDTO | null
    conducto: Conducto | ''
    neta: string
    derecho: string
    financiamiento: string
    /** Solo se usa cuando el IVA se captura a mano (o en vida, que no lo calcula). */
    iva: string
    pct: string
    comisionMonto: string
}

export type AmountField = 'neta' | 'derecho' | 'financiamiento' | 'iva' | 'comisionMonto'

/** Qué capturó el usuario por último en comisión; el otro valor se deriva de la prima neta. */
export type ComisionMode = 'pct' | 'monto'

export type PdfState = 'idle' | 'reading' | 'done'

export interface PolicyFormState {
    ramo: RamoKey | ''
    step: number
    maxStep: number
    tried: boolean
    triedSec: Record<number, boolean>
    pdf: PdfState
    pdfName: string
    rev: Record<string, boolean>
    finEdit: boolean
    pctEdit: boolean
    comisionMode: ComisionMode
    ivaEdit: boolean
    clienteEsAsegurado: boolean
    asegurados: AseguradoForm[]
    vehiculo: VehiculoForm
    /** Detalle del bien asegurado (daños / diversos). */
    texto: string
    prorrDer: boolean
    prorrFin: boolean
    caratula: CreatePolizaFromCaratulaRequest | null
    polizaId: number | null
    polizaIDRenovo: number | null
    generales: GeneralesForm
    recibos: ReciboForm[]
}

export interface PolicyFormCatalogs {
    aseguradoras: AseguradoraClaveResponse[]
    currency: MonedaDTO[]
    vendedores: VendedorDTO[]
    conductos: KeyValue[]
}

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

export interface ClaveDialog {
    show: boolean
    message: string
    payload: CreatePolizaFromCaratulaRequest | null
}

export type FieldErrors = Record<string, string>

export const ACCENT = '#4B44D8'

export interface PolicySteps {
    /** Texto corto bajo cada punto del stepper. */
    labels: string[]
    /** Título de la tarjeta y de la lista de avance del resumen. */
    titles: string[]
    hints: string[]
}

const DETAIL_STEP: Record<RamoKey | '', { label: string; title: string; hint: string }> = {
    '': { label: 'Asegurados', title: 'Asegurados', hint: 'Primero elige el ramo de la póliza' },
    vida: { label: 'Asegurados', title: 'Asegurados', hint: 'Quiénes están asegurados en la póliza' },
    salud: { label: 'Asegurados', title: 'Asegurados', hint: 'Titular y dependientes cubiertos' },
    autos: { label: 'Vehículo', title: 'Vehículo', hint: 'Modelo, descripción, número de serie y placas' },
    danos: { label: 'Bien asegurado', title: 'Bien asegurado', hint: 'Detalle del bien que cubre la póliza' },
}

export const policySteps = (ramo: RamoKey | ''): PolicySteps => {
    const detail = DETAIL_STEP[ramo]
    return {
        labels: ['Datos generales', detail.label, 'Primas', 'Recibos'],
        titles: ['Datos generales', detail.title, 'Primas y comisión', 'Recibos'],
        hints: ['Contratante, aseguradora, vigencia y número', detail.hint, 'Prima neta y comisión', 'Se generan automáticamente'],
    }
}

export const LAST_STEP = 3
export const NO_SERIE_MAX = 17
export const PLACAS_MAX = 10
const NOMBRE_MAX = 80
const APELLIDO_MAX = 60
const RFC_MAX = 13
/** Compañía y tipo que espera el buscador de autos de Copsis. */
const AUTO_SEARCH_COMPANY = 29
const AUTO_SEARCH_DELAY_MS = 500
const AUTO_SEARCH_MIN = 4

/** Del año siguiente hacia atrás 40 años, como en la captura anterior. */
export const MODELO_YEARS = Array.from({ length: 42 }, (_, i) => currentYear() + 1 - i)
export const RECIBO_FIELDS: ReciboField[] = ['primaNeta', 'derecho', 'financiamiento', 'iva']

export { FORMA_PAGO }

export const RAMO_META: Record<RamoKey, RamoMeta> = {
    vida: { label: 'Vida', tone: 'bg-tone-purple text-tone-purple-ink', conceptoPh: 'Ej. Vida temporal 20 años', planPh: 'Ej. Protección Total', api: 5, type: 'vida' },
    autos: { label: 'Autos', tone: 'bg-tone-blue text-tone-blue-ink', conceptoPh: 'Ej. Mazda 3 Hatchback 2022', planPh: 'Ej. Cobertura amplia', api: 1, type: 'autos' },
    salud: { label: 'Salud · gastos médicos', tone: 'bg-tone-teal text-tone-teal-ink', conceptoPh: 'Ej. GMM María Fernanda Ruiz', planPh: 'Ej. Plan Esencial', api: 3, type: 'salud' },
    danos: { label: 'Daños', tone: 'bg-tone-orange text-tone-orange-ink', conceptoPh: 'Ej. Casa habitación Zapopan', planPh: 'Ej. Hogar integral', api: 7, type: 'diversos' },
}

export const RAMO_OPTIONS = Object.entries(RAMO_META) as Array<[RamoKey, RamoMeta]>

const RAMO_FROM_API: Record<string, RamoKey> = {
    1: 'autos', auto: 'autos', autos: 'autos',
    3: 'salud', salud: 'salud',
    5: 'vida', vida: 'vida',
    7: 'danos', diversos: 'danos', daños: 'danos', danos: 'danos',
}

const COMISION_BY_RAMO: Record<RamoKey, 'comisionVida' | 'comisionAutos' | 'comisionSalud' | 'comisionDiversos'> = {
    vida: 'comisionVida',
    autos: 'comisionAutos',
    salud: 'comisionSalud',
    danos: 'comisionDiversos',
}

const DEFAULT_CONDUCTO: KeyValue = { key: 'CXC', value: 'CXC' }
const IVA_RATE = 0.16
const PDF_ERROR = 'Error al procesar la carátula.'

/** `###,##0.00` */
export const formatAmount = (value?: number | null) => formatNumber(Number(value || 0))

const num = (value: unknown) => parseFloat(String(value ?? '').replace(/[^0-9.-]/g, '')) || 0
/** Sumas en centavos enteros para que los totales no arrastren error de punto flotante. */
const toCents = (value: number) => Math.round(value * 100)
const amountText = (value?: number | null) => (value == null ? '' : formatAmount(value))
const PCT_MAX = 100
const FOLIO_MAX = 50

/** `15 ene 1990 · 34 años` */
export const nacimientoText = (iso: string) => formatWithAge(iso) || 'Sin fecha de nacimiento'

export const edadText = (iso: string) => {
    const years = yearsSince(iso)
    return years != null && years >= 0 ? `${years} años` : undefined
}
const isoDate = (value?: string | Date | null) => toIsoDate(value) || ''
const addOneYear = (iso: string) => parseDate(iso)?.plus({ years: 1 }).toISODate() || ''
export const fmtShort = (iso?: string | null) => formatSpokenDate(iso) || '—'

const generoUi = (value?: string | null): GeneroUi => {
    const lower = String(value || '').toLowerCase()
    if (lower === 'mujer' || lower === 'femenino') return 'mujer'
    if (lower === 'hombre' || lower === 'masculino') return 'hombre'
    return ''
}

const generoApi = (value?: string | null): Genero | null => generoUi(value) || null

const ramoFromApi = (value: unknown): RamoKey | undefined => RAMO_FROM_API[String(value ?? '').toLowerCase()]

export const fieldHelp = (errs: FieldErrors, rev: Record<string, boolean>, key: string, fallback?: string) => {
    if (errs[key]) return `Falta ${errs[key]}`
    if (rev[key]) return 'Revisar (dato de carátula)'
    return fallback
}

export const aseguradoraLabel = (row?: AseguradoraClaveResponse | null) =>
    row?.aseguradora?.abreviacion || row?.aseguradora?.aseguradora || 'Aseguradora'

export const claveLabel = (clave: AgenteClaveDTO) => clave.clave || String(clave.id ?? '')

export const monedaLabel = (moneda: MonedaDTO) => `${moneda.abreviacion || ''} · ${moneda.moneda || 'Moneda'}`.trim()

export const vehiculoLabel = (descripcion?: VehiculoDescripcion | string | null) => {
    if (!descripcion) return ''
    if (typeof descripcion === 'string') return descripcion
    return [descripcion.marca, descripcion.tipo, descripcion.descripcion].filter(Boolean).join(' ')
}

export const parentescoLabel = (parentesco?: Parentesco | '') =>
    PARENTESCOS.find((item) => item.id === parentesco)?.label || 'Por definir'

let aseguradoSeq = 0
const nextKey = () => {
    aseguradoSeq += 1
    return aseguradoSeq
}

const emptyAsegurado = (parentesco: Parentesco | '' = ''): AseguradoForm => ({
    key: nextKey(),
    id: null,
    parentesco,
    nombres: '',
    apPat: '',
    apMat: '',
    fecNacimiento: '',
    genero: '',
    rfc: '',
})

const emptyVehiculo = (): VehiculoForm => ({ id: null, modelo: currentYear(), descripcion: null, noSerie: '', placas: '' })

type AseguradoSource = Partial<Pick<PolizaAseguradoDTO, 'id' | 'nombres' | 'apPat' | 'apMat' | 'rfc' | 'genero' | 'fecNacimiento' | 'parentesco'>>
    | CreatePolizaAseguradoRequest

const toAseguradoForm = (asegurado: AseguradoSource, index: number): AseguradoForm => ({
    key: nextKey(),
    id: asegurado.id ?? null,
    parentesco: index === 0 ? 'titular' : asegurado.parentesco || '',
    nombres: asegurado.nombres || '',
    apPat: asegurado.apPat || '',
    apMat: asegurado.apMat || '',
    fecNacimiento: isoDate(asegurado.fecNacimiento),
    genero: generoUi(asegurado.genero),
    rfc: asegurado.rfc || '',
})

const toAseguradosForm = (list?: AseguradoSource[] | null) =>
    (list?.length ? list.map(toAseguradoForm) : [emptyAsegurado('titular')])

const toVehiculoForm = (vehiculo?: PolizaVehiculoDTO | CreatePolizaVehiculoRequest | null): VehiculoForm => {
    if (!vehiculo) return emptyVehiculo()
    const hasDescripcion = vehiculo.descripcion || vehiculo.marca || vehiculo.tipo
    return {
        id: vehiculo.id ?? null,
        modelo: vehiculo.modelo || currentYear(),
        descripcion: hasDescripcion
            ? { descripcion: vehiculo.descripcion || '', marca: vehiculo.marca || '', tipo: vehiculo.tipo || '', amis: vehiculo.amis || '' }
            : null,
        noSerie: vehiculo.noSerie || '',
        placas: vehiculo.placas || '',
    }
}

/** El titular toma los datos del contratante; conserva su `id` de póliza. */
const titularFromContratante = (current: AseguradoForm, contratante: Contratante): AseguradoForm => ({
    ...current,
    parentesco: 'titular',
    nombres: contratante.nombres || '',
    apPat: contratante.apPat || '',
    apMat: contratante.apMat || '',
    fecNacimiento: isoDate(contratante.fecNacimiento),
    genero: generoUi(contratante.sexo),
    rfc: contratante.rfc || '',
})

const RFC_FIELDS: AseguradoField[] = ['nombres', 'apPat', 'apMat', 'fecNacimiento']

const withRfc = (asegurado: AseguradoForm): AseguradoForm => {
    const nacimiento = parseDate(asegurado.fecNacimiento)
    if (!nacimiento || !asegurado.nombres.trim() || !asegurado.apPat.trim()) return asegurado
    try {
        const rfc: string | undefined = calcularRFC(asegurado.apPat, asegurado.apMat, asegurado.nombres, nacimiento.toFormat('yyyy/MM/dd'))
        return rfc ? { ...asegurado, rfc } : asegurado
    } catch (error) {
        console.log('invalida RFC', error)
        return asegurado
    }
}

const sanitizeAsegurado = (field: AseguradoField, value: string) => {
    if (field === 'nombres') return clip(value, NOMBRE_MAX)
    if (field === 'apPat' || field === 'apMat') return clip(value, APELLIDO_MAX)
    if (field === 'rfc') return clip(value, RFC_MAX).toUpperCase()
    return value
}

const initialState = (): PolicyFormState => ({
    ramo: '',
    step: 0,
    maxStep: 0,
    tried: false,
    triedSec: {},
    pdf: 'idle',
    pdfName: '',
    rev: {},
    finEdit: false,
    pctEdit: false,
    comisionMode: 'pct',
    ivaEdit: false,
    clienteEsAsegurado: false,
    asegurados: [emptyAsegurado('titular')],
    vehiculo: emptyVehiculo(),
    texto: '',
    prorrDer: false,
    prorrFin: false,
    caratula: null,
    polizaId: null,
    polizaIDRenovo: null,
    generales: {
        contratante: null,
        aseguradora: '',
        agente: '',
        moneda: '',
        formaPago: 'contado',
        desde: '',
        hasta: '',
        numero: '',
        concepto: '',
        plan: '',
        vendedor: '',
        vendedorEntity: null,
        conducto: 'CXC',
        neta: '',
        derecho: '',
        financiamiento: '',
        iva: '',
        pct: '',
        comisionMonto: '',
    },
    recibos: [],
})

/** Como antes: el cliente de la carátula se selecciona exista o no en la cartera. */
const toContratante = (cliente?: CreateClientRequest | null): Contratante | null => {
    if (!cliente || !(cliente.id || cliente.nombres?.trim())) return null
    return {
        id: cliente.id ?? null,
        nuevo: cliente.id ? null : cliente,
        nombres: cliente.nombres || null,
        apPat: cliente.apPat ?? null,
        apMat: cliente.apMat ?? null,
        rfc: cliente.rfc ?? null,
        sexo: cliente.sexo ?? null,
        fiscal: cliente.fiscal ?? null,
        edoCivil: cliente.edoCivil ?? null,
        fecNacimiento: cliente.fecNacimiento ?? null,
        esRobot: cliente.esRobot ?? null,
    }
}

/** Con id se edita el cliente; sin id (carátula) se abre el alta con lo que se leyó de la carátula. */
const clientePrefill = (c: Contratante | null): Partial<CustomerFormState> | undefined => {
    if (!c) return undefined
    if (c.id) return { id: c.id }
    return {
        personalidad: c.fiscal || 'fisica',
        nombres: c.nombres || '',
        apPat: c.apPat || '',
        apMat: c.apMat || '',
        rfc: c.rfc || '',
        sexo: c.sexo || '',
        edoCivil: c.edoCivil || '',
        fecNacimiento: toDate(c.fecNacimiento),
        cp: c.nuevo?.direccion?.cp || '',
        direccion: c.nuevo?.direccion?.direccion || '',
    }
}

const toReciboForm = (recibo: ReciboDTO): ReciboForm => ({
    id: recibo.id,
    serie: recibo.serie,
    totalSerie: recibo.totalSerie,
    primaNeta: recibo.prima || 0,
    derecho: recibo.derecho || 0,
    financiamiento: recibo.recargo || 0,
    iva: recibo.iva || 0,
    primaTotal: recibo.total || 0,
    fecInicial: recibo.fecVigDe,
    fecFinal: recibo.fecVigA,
    vencimiento: recibo.fecVence,
    status: recibo.status || 'pendiente',
    folioPago: recibo.folioPago || '',
    fecPago: recibo.fecPago || null,
})

const sameId = (left: unknown, right: unknown) => String(left ?? '') === String(right ?? '')

const readJson = async <T>(request: Promise<Response>): Promise<WrapperResponse<T>> => (await request).json()

const defaultMoneda = (currency: MonedaDTO[]): number | '' =>
    currency.find((m) => String(m.abreviacion || '').toUpperCase() === 'MXN')?.id || currency[0]?.id || ''

const defaultConducto = (conductos: KeyValue[]): Conducto => (conductos[0]?.key as Conducto | undefined) || 'CXC'

export const usePolicyFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const locationState = location.state as PolicyFormLocationState | null
    const { polizaId: routePolizaId } = useParams()
    const backUrl = locationState?.backUrl || POLICIES_PATH
    const isEdit = Boolean(routePolizaId && routePolizaId !== 'nuevo' && !Number.isNaN(Number(routePolizaId)))
    const editId = isEdit ? Number(routePolizaId) : null
    const renewId = locationState?.action === 'renovar' ? locationState.polizaId ?? null : null

    const [state, setState] = useState<PolicyFormState>(initialState)
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [catalogs, setCatalogs] = useState<PolicyFormCatalogs>({ aseguradoras: [], currency: [], vendedores: [], conductos: [] })
    const [clientOptions, setClientOptions] = useState<Contratante[]>([])
    const [clientSearching, setClientSearching] = useState(false)
    const [clientDialog, setClientDialog] = useState(false)
    const [claveDialog, setClaveDialog] = useState<ClaveDialog>({ show: false, message: '', payload: null })
    const [resetDialog, setResetDialog] = useState(false)
    const [clienteIncompletoDialog, setClienteIncompletoDialog] = useState(false)
    const [vehiculoOptions, setVehiculoOptions] = useState<VehiculoOption[]>([])
    const [vehiculoInput, setVehiculoInput] = useState('')
    const [vehiculoSearching, setVehiculoSearching] = useState(false)
    const [reciboDraft, setReciboDraft] = useState<{ index: number; field: ReciboField; value: string } | null>(null)
    const [expandedRecibo, setExpandedRecibo] = useState<number | null>(null)
    const [applyAllIndex, setApplyAllIndex] = useState<number | null>(null)
    const [adjuntosOpen, setAdjuntosOpen] = useState(false)
    const clientTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const vehiculoTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const loadedRef = useRef(false)

    const patch = useCallback((partial: Partial<PolicyFormState>) => setState((current) => ({ ...current, ...partial })), [])
    const patchG = useCallback((partial: Partial<GeneralesForm>) => setState((current) => ({
        ...current,
        generales: { ...current.generales, ...partial },
        rev: Object.keys(partial).reduce((acc, key) => ({ ...acc, [key]: false }), current.rev),
    })), [])

    const ramoMeta = state.ramo ? RAMO_META[state.ramo] : null
    const aseguradoraRow = catalogs.aseguradoras.find((row) => sameId(row.aseguradora?.id, state.generales.aseguradora))
    const claves = useMemo(() => aseguradoraRow?.claves || [], [aseguradoraRow])

    const effClave: number | '' = state.generales.agente || (claves.length === 1 ? claves[0].id ?? '' : '')
    const claveAuto = !state.generales.agente && claves.length === 1 && !!state.generales.aseguradora
    const hasta = state.finEdit ? state.generales.hasta : (state.generales.hasta || addOneYear(state.generales.desde))
    const finAuto = !state.finEdit && !!state.generales.desde

    const suggestedPct = useMemo(() => {
        if (state.pctEdit || !effClave || !state.ramo) return state.generales.pct
        const clave = claves.find((item) => sameId(item.id, effClave))
        if (!clave) return state.generales.pct
        const value = clave[COMISION_BY_RAMO[state.ramo]]
        return value == null ? state.generales.pct : String(value)
    }, [state.pctEdit, state.generales.pct, state.ramo, effClave, claves])

    const neta = num(state.generales.neta)
    const derecho = num(state.generales.derecho)
    const financiamiento = num(state.generales.financiamiento)
    const subtotalCents = toCents(neta) + toCents(derecho) + toCents(financiamiento)
    // Como en la captura anterior: vida no calcula IVA; los demás lo recalculan al cambiar primas salvo que se edite.
    const ivaManual = state.ramo === 'vida' || state.ivaEdit
    const ivaCents = ivaManual ? toCents(num(state.generales.iva)) : Math.round(subtotalCents * IVA_RATE)
    const iva = ivaCents / 100
    const total = (subtotalCents + ivaCents) / 100
    const ivaValue = ivaManual ? state.generales.iva : (subtotalCents ? formatAmount(iva) : '')
    const ivaHint = state.ramo === 'vida'
        ? 'Vida no calcula IVA; captúralo si la póliza lo trae.'
        : state.ivaEdit ? 'Capturado a mano; se recalcula si cambias las primas.' : 'Calculado: 16% de prima neta + derecho + financiamiento.'

    const porMonto = state.comisionMode === 'monto'
    const comisionMontoN = num(state.generales.comisionMonto)
    const pctN = porMonto ? (neta > 0 ? (comisionMontoN * 100) / neta : 0) : (parseFloat(suggestedPct) || 0)
    const comision = porMonto ? comisionMontoN : toCents((neta * pctN) / 100) / 100
    const pctValue = porMonto ? (state.generales.comisionMonto.trim() ? formatPct(pctN) : '') : suggestedPct
    const comisionValue = porMonto ? state.generales.comisionMonto : (suggestedPct !== '' && neta > 0 ? formatAmount(comision) : '')
    const pctAuto = !state.pctEdit && !porMonto && !!suggestedPct && !!effClave
    const forma = FORMA_PAGO.find((item) => item.key === state.generales.formaPago) || FORMA_PAGO[0]

    const errors = useCallback((sec: number) => {
        const e: FieldErrors = {}
        const g = state.generales
        if (sec === 0) {
            if (!state.ramo) e.ramo = 'ramo'
            if (!g.contratante || !(g.contratante.id || g.contratante.nombres?.trim())) e.contratante = 'contratante'
            if (!g.aseguradora) e.aseguradora = 'aseguradora'
            if (!effClave) e.clave = 'clave de agente'
            if (!g.formaPago) e.forma = 'forma de pago'
            if (!g.desde) e.desde = 'inicio de vigencia'
            if (!hasta) e.hasta = 'fin de vigencia'
            if (!g.numero.trim()) e.numero = 'no. de póliza'
            if (!g.plan.trim()) e.plan = 'plan / paquete'
            if (!g.concepto.trim()) e.concepto = 'concepto'
        }
        if (sec === 1) {
            if (state.ramo === 'vida' || state.ramo === 'salud') {
                state.asegurados.forEach((a, i) => {
                    const who = i === 0 ? 'titular' : `asegurado ${i + 1}`
                    if (state.ramo === 'salud' && i > 0 && !a.parentesco) e[`a${i}parentesco`] = `parentesco (${who})`
                    if (!a.nombres.trim()) e[`a${i}nombres`] = `nombres (${who})`
                    if (!a.apPat.trim()) e[`a${i}apPat`] = `apellido paterno (${who})`
                    if (!a.genero) e[`a${i}genero`] = `género (${who})`
                })
            }
            if (state.ramo === 'autos') {
                if (!state.vehiculo.modelo) e.vModelo = 'modelo del vehículo'
                if (!vehiculoLabel(state.vehiculo.descripcion).trim()) e.vDescripcion = 'descripción del vehículo'
                if (!state.vehiculo.noSerie.trim()) e.vNoSerie = 'no. de serie'
            }
            if (state.ramo === 'danos' && !state.texto.trim()) e.texto = 'detalle del bien asegurado'
        }
        if (sec === 2) {
            if (!(neta > 0)) e.neta = 'prima neta'
            const sinComision = state.comisionMode === 'monto'
                ? !state.generales.comisionMonto.trim()
                : suggestedPct === '' || Number.isNaN(parseFloat(suggestedPct))
            if (sinComision) e.pct = 'comisión'
        }
        return e
    }, [state, effClave, hasta, neta, suggestedPct])

    const allErr = useMemo(() => [0, 1, 2].map((i) => errors(i)), [errors])
    const shownErr = (i: number) => ((state.tried || state.triedSec[i]) ? allErr[i] : {})
    const se: FieldErrors = { ...shownErr(0), ...shownErr(1), ...shownErr(2) }
    const okSec = (i: number) => (i < LAST_STEP ? Object.keys(allErr[i]).length === 0 : neta > 0)
    const errorCount = (i: number) => (i < LAST_STEP ? Object.keys(allErr[i]).length : 0)

    const steps = policySteps(state.ramo)
    const [titular, ...dependientes] = state.asegurados
    const titularNombre = titular ? [titular.nombres, titular.apPat].filter(Boolean).join(' ') : ''

    const detailSub = () => {
        if (state.ramo === 'autos') {
            return [vehiculoLabel(state.vehiculo.descripcion), state.vehiculo.modelo, state.vehiculo.noSerie].filter(Boolean).join(' · ')
        }
        if (state.ramo === 'danos') return state.texto.trim()
        return [titularNombre && `Titular: ${titularNombre}`, dependientes.length ? `+${dependientes.length} asegurado(s)` : ''].filter(Boolean).join(' · ')
    }

    const subs = [
        [
            state.generales.contratante && personName(state.generales.contratante),
            aseguradoraRow?.aseguradora?.abreviacion || aseguradoraRow?.aseguradora?.aseguradora,
            forma.label,
            state.generales.numero,
        ].filter(Boolean).join(' · '),
        detailSub(),
        neta ? `Prima total $${formatAmount(total)} · comisión ${formatPct(pctN)}% ($${formatAmount(comision)})` : '',
        `${state.recibos.length || forma.parcialidades} recibo(s)`,
    ]

    const sumRecCents = state.recibos.reduce((acc, r) => acc + toCents(num(r.primaTotal)), 0)
    const sumRec = sumRecCents / 100
    const recDiff = neta > 0 && state.recibos.length > 0 && sumRecCents !== toCents(total)
    const errList = state.tried ? allErr.flatMap((e) => Object.values(e)) : []

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const [aseg, monedas, vendedores, conductos] = await Promise.all([
                    readJson<AseguradoraClaveResponse[]>(APIInvoke.invokeGET('/aseguradoras/claves')),
                    readJson<MonedaDTO[]>(APIInvoke.invokeGET('/monedas')),
                    readJson<PagaebleResult<VendedorDTO>>(APIInvoke.invokeGET('/vendedores')),
                    readJson<KeyValue[]>(APIInvoke.invokeGET('/catalogos/polizas/conductos')),
                ])
                if (!active) return
                const currency = monedas.body || []
                const conductoList = conductos.body || []
                setCatalogs({
                    aseguradoras: aseg.body || [],
                    currency,
                    vendedores: vendedores.body?.items || [],
                    conductos: conductoList,
                })
                setState((current) => ({
                    ...current,
                    generales: {
                        ...current.generales,
                        moneda: current.generales.moneda || defaultMoneda(currency),
                        conducto: current.generales.conducto || defaultConducto(conductoList),
                    },
                }))
            } catch (error) {
                console.error(error)
                if (active) setMessage({ type: 'error', message: 'No se pudieron cargar los catálogos.' })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [])

    const applyPoliza = (pay: PolizaDTO, { renew }: { renew: boolean }) => {
        const ramoKey = ramoFromApi(pay.ramo?.ramo) || ramoFromApi(pay.ramo?.id) || 'vida'
        setState((current) => ({
            ...current,
            ramo: ramoKey,
            polizaId: renew ? null : pay.id,
            polizaIDRenovo: renew ? pay.id : pay.polizaIDRenovo || null,
            clienteEsAsegurado: false,
            asegurados: toAseguradosForm(pay.asegurados),
            vehiculo: toVehiculoForm(pay.vehiculo),
            texto: pay.texto || '',
            finEdit: true,
            pctEdit: true,
            comisionMode: 'pct',
            // Como en la captura anterior: al editar, el IVA se recalcula al 16% salvo en vida, que conserva el guardado.
            ivaEdit: false,
            generales: {
                ...current.generales,
                contratante: pay.cliente,
                aseguradora: pay.agenteClave?.aseguradora?.id ?? '',
                agente: pay.agenteClave?.id ?? '',
                moneda: pay.moneda?.id ?? current.generales.moneda,
                formaPago: pay.formaPago || 'contado',
                desde: renew ? isoDate(pay.fechaVigenciaA) : isoDate(pay.fechaVigenciaDe),
                hasta: renew ? addOneYear(isoDate(pay.fechaVigenciaA)) : isoDate(pay.fechaVigenciaA),
                numero: pay.noPoliza || '',
                concepto: pay.concepto || '',
                plan: pay.paquete || '',
                vendedor: pay.vendedor?.id ?? '',
                vendedorEntity: pay.vendedor || null,
                conducto: pay.conducto || 'CXC',
                neta: amountText(pay.prima),
                derecho: amountText(pay.derecho),
                financiamiento: amountText(pay.recargo),
                iva: amountText(pay.iva),
                pct: String(pay.porcentajeComision ?? ''),
                comisionMonto: '',
            },
            recibos: renew ? [] : (pay.recibos || []).map(toReciboForm),
        }))
        if (pay.cliente) setClientOptions([pay.cliente])
    }

    useEffect(() => {
        if (loadedRef.current) return undefined
        const loadId = editId || renewId
        if (!loadId) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const response = await readJson<PolizaDTO>(APIInvoke.invokeGET(`/polizas/${loadId}`))
                if (!active) return
                if (!response?.ok || !response.body) throw new Error(response?.message || 'No se pudo cargar la póliza.')
                applyPoliza(response.body, { renew: Boolean(renewId) })
                loadedRef.current = true
            } catch (error) {
                console.error(error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, 'No se pudo cargar la póliza.') })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [editId, renewId])

    useEffect(() => {
        const onlyClave = claves.length === 1 ? claves[0].id : null
        if (onlyClave != null && !state.generales.agente && state.generales.aseguradora) {
            patchG({ agente: onlyClave })
        }
    }, [claves, state.generales.agente, state.generales.aseguradora, patchG])

    useEffect(() => () => {
        clearTimeout(clientTimer.current)
        clearTimeout(vehiculoTimer.current)
    }, [])

    /** Cada carátula reinicia el formulario con sus datos y regresa a Datos generales; solo se conserva el contexto (renovación). */
    const applyCaratula = (body: CreatePolizaRequest, fileMeta: CreatePolizaFromCaratulaRequest) => {
        const ramoKey = ramoFromApi(body.ramo) || state.ramo || 'vida'
        const cliente = toContratante(body.cliente)
        const vehiculo = body.vehiculo ? toVehiculoForm(body.vehiculo) : emptyVehiculo()
        const rev: Record<string, boolean> = { plan: true, neta: true }
        if (!cliente?.id) rev.contratante = true
        const base = initialState()
        setState((current) => ({
            ...base,
            polizaIDRenovo: current.polizaIDRenovo,
            ramo: ramoKey,
            pdf: 'done',
            pdfName: fileMeta.fileName,
            rev,
            caratula: fileMeta,
            asegurados: toAseguradosForm(body.asegurados),
            vehiculo,
            texto: body.texto || '',
            finEdit: Boolean(body.fechaVigenciaA),
            ivaEdit: body.iva != null,
            prorrDer: !!body.prorratearDerecho,
            prorrFin: !!body.prorratearRecargo,
            generales: {
                ...base.generales,
                contratante: cliente,
                aseguradora: body.aseguradora || '',
                agente: body.claveAgente || '',
                moneda: body.moneda || defaultMoneda(catalogs.currency),
                formaPago: body.formaPago || base.generales.formaPago,
                conducto: defaultConducto(catalogs.conductos),
                desde: isoDate(body.fechaVigenciaDe),
                hasta: isoDate(body.fechaVigenciaA),
                numero: body.noPoliza || '',
                concepto: body.concepto || '',
                plan: String(body.paquete || '').slice(0, 50),
                neta: amountText(body.prima),
                derecho: amountText(body.derecho),
                financiamiento: amountText(body.recargo),
                iva: amountText(body.iva),
            },
        }))
        setClientOptions(cliente ? [cliente] : [])
        setVehiculoInput(vehiculoLabel(vehiculo.descripcion))
        setVehiculoOptions([])
        setReciboDraft(null)
        setExpandedRecibo(null)
        setApplyAllIndex(null)
    }

    const searchClientes = (text: string) => {
        clearTimeout(clientTimer.current)
        if (text.trim().length < 3) {
            setClientOptions(state.generales.contratante ? [state.generales.contratante] : [])
            setClientSearching(false)
            return
        }
        setClientSearching(true)
        clientTimer.current = setTimeout(async () => {
            try {
                const response = await readJson<PagaebleResult<ClienteDTO>>(APIInvoke.invokeGET(`/clientes?nombre=${encodeURIComponent(text)}&size=20&order=nombres%20asc`))
                setClientOptions(response?.ok ? (response.body?.items || []) : [])
            } catch (error) {
                console.error(error)
                setClientOptions([])
            } finally {
                setClientSearching(false)
            }
        }, 400)
    }

    const previewCaratula = (payload: CreatePolizaFromCaratulaRequest) =>
        readJson<CreatePolizaRequest>(APIInvoke.invokePOST('/polizas/caratula/preview', payload, { timeoutMs: LONG_TIMEOUT_MS }))

    const uploadPdf = async (file?: File | null) => {
        if (!file) return
        try {
            patch({ pdf: 'reading', pdfName: file.name })
            setMessage(null)
            const body = new FormData()
            body.append('bucket', config.bucket.name)
            body.append('path', `polizas/${file.name}`)
            body.append('file', file)
            const uploading: WrapperResponse<StorageFile> = await (await APIInvoke.uploadStorage(body)).json()
            if (uploading.ok !== true || !uploading.body) throw new Error(uploading.message || 'No se pudo subir el archivo.')
            const payload: CreatePolizaFromCaratulaRequest = { fileName: file.name, caratulaUrl: uploading.body.url }
            const caratulaResponse = await previewCaratula(payload)
            if (caratulaResponse.ok === false) {
                if (caratulaResponse.code === 10) {
                    setClaveDialog({
                        show: true,
                        message: `${caratulaResponse.message}, ¿Desea continuar y crear automáticamente la clave de agente?`,
                        payload: { ...payload, crearClaveAgente: true },
                    })
                    patch({ pdf: 'idle' })
                    return
                }
                throw new Error(caratulaResponse.message || 'No se pudo leer la carátula.')
            }
            if (!caratulaResponse.body) throw new Error('No se pudo leer la carátula.')
            applyCaratula(caratulaResponse.body, payload)
        } catch (error) {
            console.error(error)
            patch({ pdf: 'idle' })
            setMessage({ type: 'error', message: errorMessage(error, PDF_ERROR) })
        }
    }

    const closeClaveDialog = () => setClaveDialog({ show: false, message: '', payload: null })

    const previewWithClave = async () => {
        const payload = claveDialog.payload
        if (!payload) return
        try {
            closeClaveDialog()
            setLoading(true)
            const caratulaResponse = await previewCaratula(payload)
            if (caratulaResponse.ok === false || !caratulaResponse.body) throw new Error(caratulaResponse.message || PDF_ERROR)
            const aseg = await readJson<AseguradoraClaveResponse[]>(APIInvoke.invokeGET('/aseguradoras/claves'))
            setCatalogs((current) => ({ ...current, aseguradoras: aseg.body || [] }))
            applyCaratula(caratulaResponse.body, payload)
        } catch (error) {
            console.error(error)
            setMessage({ type: 'error', message: errorMessage(error, PDF_ERROR) })
        } finally {
            setLoading(false)
        }
    }

    const calcularRecibos = async () => {
        try {
            setLoading(true)
            const request: CalcularRecibosRequest = {
                formaPago: state.generales.formaPago,
                modelo: ramoMeta?.type || state.ramo,
                pagos: forma.parcialidades,
                fechaVigenciaDe: state.generales.desde,
                fechaVigenciaA: hasta,
                primaNeta: neta,
                derecho,
                recargo: financiamiento,
                iva,
                primaTotal: total,
                primaPrimerRecibo: 0,
                primaSubsecuenteRecibo: 0,
                prorratearDerecho: state.prorrDer,
                prorratearRecargo: state.prorrFin,
            }
            const response = await readJson<ReciboDTO[]>(APIInvoke.invokePOST('/recibos/calcular', request))
            if (!response?.ok && !Array.isArray(response?.body)) {
                throw new Error(response?.message || 'No se pudieron calcular los recibos.')
            }
            const recibos = (response.body || []).map(toReciboForm)
            patch({ recibos })
            return recibos
        } catch (error) {
            console.error(error)
            setMessage({ type: 'error', message: errorMessage(error, 'Error al calcular recibos.') })
            return null
        } finally {
            setLoading(false)
        }
    }

    const parentescoOf = (asegurado: AseguradoForm, index: number): Parentesco => {
        if (index === 0) return 'titular'
        if (state.ramo === 'vida') return 'otro'
        return asegurado.parentesco || 'otro'
    }

    const buildAsegurados = (): CreatePolizaAseguradoRequest[] | undefined => {
        if (state.ramo !== 'vida' && state.ramo !== 'salud') return undefined
        // En POST el backend toma `id` como el de Asegurado; solo el PUT espera el de PolizaAsegurado.
        const keepIds = Boolean(state.polizaId)
        return state.asegurados.map((a, index) => ({
            id: keepIds ? a.id : null,
            parentesco: parentescoOf(a, index),
            nombres: a.nombres.trim(),
            apPat: a.apPat.trim(),
            apMat: a.apMat.trim(),
            fecNacimiento: a.fecNacimiento || null,
            genero: generoApi(a.genero),
            rfc: a.rfc.trim().toUpperCase(),
        }))
    }

    const buildVehiculo = (): CreatePolizaVehiculoRequest | undefined => {
        if (state.ramo !== 'autos') return undefined
        const { id, modelo, descripcion, noSerie, placas } = state.vehiculo
        return {
            id,
            modelo,
            marca: descripcion?.marca || '',
            tipo: descripcion?.tipo || '',
            amis: descripcion?.amis || '',
            descripcion: descripcion?.descripcion || '',
            noSerie: noSerie.trim().toUpperCase(),
            placas: placas.trim().toUpperCase(),
        }
    }

    const guardar = async () => {
        if (allErr.some((e) => Object.keys(e).length)) {
            const first = allErr.findIndex((e) => Object.keys(e).length)
            patch({ tried: true, step: first, maxStep: Math.max(state.maxStep, first) })
            return
        }
        const c = state.generales.contratante
        if (!c || !ramoMeta) return
        let recibos: ReciboForm[] | null = state.recibos
        if (!recibos.length) {
            recibos = await calcularRecibos()
            if (!recibos) return
        }
        try {
            setLoading(true)
            setMessage(null)
            const request: CreatePolizaRequest = {
                polizaIDRenovo: state.polizaIDRenovo,
                prima: neta,
                ajuste: 0,
                derecho,
                recargo: financiamiento,
                iva,
                // El backend solo guarda el %, y el monto lo recalcula como % × prima neta; por eso, si se capturó el
                // monto, el % lleva 6 decimales. No enviar `comisiones`: el API lo valida como porcentaje (@Max(100)).
                porcentajeComision: Number(pctN.toFixed(6)),
                total,
                fechaVigenciaDe: state.generales.desde,
                fechaVigenciaA: hasta,
                status: 'vigente',
                formaPago: state.generales.formaPago,
                noPoliza: state.generales.numero.toUpperCase(),
                claveAgente: effClave || null,
                ramo: ramoMeta.api,
                moneda: Number(state.generales.moneda),
                concepto: state.generales.concepto,
                texto: state.ramo === 'danos' ? state.texto.trim() : undefined,
                paquete: state.generales.plan,
                cliente: c.id
                    ? {
                        id: c.id,
                        nombres: c.nombres || '',
                        apPat: c.apPat,
                        apMat: c.apMat,
                        fecNacimiento: null,
                        fiscal: c.fiscal,
                        rfc: c.rfc,
                        edoCivil: c.edoCivil,
                        sexo: c.sexo,
                    }
                    : { ...c.nuevo, id: null, nombres: c.nombres || '' },
                vendedor: state.generales.vendedorEntity || undefined,
                conducto: state.generales.conducto || null,
                asegurados: buildAsegurados(),
                vehiculo: buildVehiculo(),
                recibos: recibos.map((recibo) => ({
                    id: recibo.id,
                    total: num(recibo.primaTotal),
                    observacion: '',
                    recargo: num(recibo.financiamiento),
                    fecVigDe: isoDate(recibo.fecInicial),
                    fecPago: recibo.fecPago ? isoDate(recibo.fecPago) : null,
                    tipoCambio: Number(state.generales.moneda),
                    folioPago: recibo.folioPago || '',
                    status: recibo.status || 'pendiente',
                    folioAseguradora: '',
                    derecho: num(recibo.derecho),
                    prima: num(recibo.primaNeta),
                    serie: recibo.serie,
                    comision: 0,
                    fecConciliacion: null,
                    iva: num(recibo.iva),
                    fecVigA: isoDate(recibo.fecFinal),
                    totalSerie: recibo.serie,
                    folioConciliacion: '',
                    fecVence: isoDate(recibo.vencimiento),
                    ajuste: 0,
                })),
                caratula: state.caratula?.caratulaUrl
                    ? { caratulaUrl: state.caratula.caratulaUrl, filename: state.caratula.fileName }
                    : undefined,
            }
            const response = state.polizaId
                ? await readJson<PolizaDTO>(APIInvoke.invokePUT(`/polizas/${state.polizaId}`, request))
                : await readJson<PolizaDTO>(APIInvoke.invokePOST('/polizas', request))
            if (response.ok) {
                const savedId = response.body?.id || state.polizaId
                const numero = response.body?.noPoliza || state.generales.numero
                if (!savedId) {
                    history.replace(backUrl)
                    return
                }
                const readState: PolicyReadLocationState = { savedMessage: `Póliza ${numero || ''} guardada`.replace(/\s+/g, ' ') }
                history.replace(policyPath(savedId), readState)
            } else {
                setMessage({ type: 'error', message: response.message || 'No se pudo guardar la póliza.' })
            }
        } catch (error) {
            console.error(error)
            setMessage({ type: 'error', message: 'Error al guardar la póliza.' })
        } finally {
            setLoading(false)
        }
    }

    const stepNext = async () => {
        const i = state.step
        if (i < LAST_STEP && Object.keys(allErr[i]).length) {
            patch({ triedSec: { ...state.triedSec, [i]: true } })
            return
        }
        // Al editar se conservan los recibos de la póliza (el PUT los actualiza por id y no crea ni borra).
        // En alta se recalculan salvo que ya se hayan editado a mano; para eso está Recalcular.
        const recalcular = !state.polizaId && (!state.recibos.length || !state.recibos.some((r) => r.edited))
        if (i === 2 && recalcular) {
            const recibos = await calcularRecibos()
            if (!recibos) return
        }
        if (i === LAST_STEP) {
            await guardar()
            return
        }
        patch({ step: i + 1, maxStep: Math.max(state.maxStep, i + 1) })
    }

    const changeRecibo = (index: number, field: ReciboField, value: string) => {
        const clean = sanitizeDecimal(value)
        setReciboDraft({ index, field, value: clean })
        setState((current) => ({
            ...current,
            recibos: current.recibos.map((r, i) => {
                if (i !== index) return r
                const next = { ...r, [field]: num(clean), edited: true }
                if (field !== 'iva' && current.ramo !== 'vida') {
                    next.iva = Math.round((toCents(next.primaNeta) + toCents(next.derecho) + toCents(next.financiamiento)) * IVA_RATE) / 100
                }
                next.primaTotal = (toCents(next.primaNeta) + toCents(next.derecho) + toCents(next.financiamiento) + toCents(next.iva)) / 100
                return next
            }),
        }))
    }

    /** Mientras se edita una celda se muestra lo tecleado; fuera de foco, `###,##0.00`. */
    const reciboValue = (index: number, field: ReciboField) => (
        reciboDraft && reciboDraft.index === index && reciboDraft.field === field
            ? reciboDraft.value
            : formatAmount(state.recibos[index]?.[field])
    )

    /** Al marcarlo pagado propone la fecha de hoy y abre el detalle para capturar folio; al desmarcarlo limpia el pago. */
    const togglePagado = (index: number) => {
        const pagar = state.recibos[index]?.status !== 'pagado'
        setState((current) => ({
            ...current,
            recibos: current.recibos.map((r, i) => {
                if (i !== index) return r
                return pagar
                    ? { ...r, status: 'pagado', fecPago: r.fecPago || todayIso() }
                    : { ...r, status: 'pendiente', fecPago: null, folioPago: '' }
            }),
        }))
        if (pagar) setExpandedRecibo(index)
    }

    const changeReciboDate = (index: number, field: ReciboDateField, value: string) => {
        setState((current) => ({
            ...current,
            recibos: current.recibos.map((r, i) => (i === index ? { ...r, [field]: value || null } : r)),
        }))
    }

    const changeFolioPago = (index: number, value: string) => {
        setState((current) => ({
            ...current,
            recibos: current.recibos.map((r, i) => (i === index ? { ...r, folioPago: onlySimple(value, FOLIO_MAX) } : r)),
        }))
    }

    /** Igual que antes: copia los montos del recibo elegido a todos los recibos después del primero. */
    const applyToAll = () => {
        const index = applyAllIndex
        setApplyAllIndex(null)
        if (index == null) return
        setState((current) => {
            const source = current.recibos[index]
            if (!source) return current
            return {
                ...current,
                recibos: current.recibos.map((r, i) => (i === 0 ? r : {
                    ...r,
                    primaNeta: source.primaNeta,
                    derecho: source.derecho,
                    financiamiento: source.financiamiento,
                    iva: source.iva,
                    primaTotal: source.primaTotal,
                    edited: true,
                })),
            }
        })
    }

    const reiniciar = () => {
        setResetDialog(false)
        setState(initialState())
        setMessage(null)
        loadedRef.current = false
        const nextState: PolicyFormLocationState = { backUrl }
        history.push(NEW_POLICY_PATH, nextState)
    }

    const toId = (value: string | number): number | '' => (value === '' ? '' : Number(value))

    /** Si el titular venía del contratante, lo sigue; un cliente incompleto (robot) lo desliga. */
    const setContratante = (value: Contratante | null) => {
        patchG({ contratante: value })
        setState((current) => {
            if (!current.clienteEsAsegurado) return current
            const [first, ...rest] = current.asegurados
            if (!value || value.esRobot) {
                return { ...current, clienteEsAsegurado: false, asegurados: [{ ...emptyAsegurado('titular'), id: first.id }, ...rest] }
            }
            return { ...current, asegurados: [titularFromContratante(first, value), ...rest] }
        })
    }

    const toggleClienteEsAsegurado = () => {
        const c = state.generales.contratante
        if (!state.clienteEsAsegurado && c?.esRobot) {
            setClienteIncompletoDialog(true)
            return
        }
        setState((current) => {
            const [first, ...rest] = current.asegurados
            const checked = !current.clienteEsAsegurado && !!c
            const titularNuevo = checked && c ? titularFromContratante(first, c) : { ...emptyAsegurado('titular'), id: first.id }
            return { ...current, clienteEsAsegurado: checked, asegurados: [titularNuevo, ...rest] }
        })
    }

    const changeAsegurado = (key: number, field: AseguradoField, value: string) => {
        setState((current) => ({
            ...current,
            asegurados: current.asegurados.map((a) => {
                if (a.key !== key) return a
                const next = { ...a, [field]: sanitizeAsegurado(field, value) }
                return RFC_FIELDS.includes(field) ? withRfc(next) : next
            }),
        }))
    }

    /** El cónyuge solo puede aparecer una vez y el titular siempre es el primero. */
    const parentescoOptions = (key: number) => {
        const otherConyuge = state.asegurados.some((a) => a.key !== key && a.parentesco === 'conyuge')
        return PARENTESCOS.filter((item) => item.id !== 'titular' && !(otherConyuge && item.id === 'conyuge'))
    }

    const patchVehiculo = (partial: Partial<VehiculoForm>) => setState((current) => ({ ...current, vehiculo: { ...current.vehiculo, ...partial } }))

    const searchVehiculos = (text: string) => {
        clearTimeout(vehiculoTimer.current)
        const description = text.trim()
        if (description.length < AUTO_SEARCH_MIN) {
            setVehiculoOptions([])
            setVehiculoSearching(false)
            return
        }
        setVehiculoSearching(true)
        const modelo = state.vehiculo.modelo
        vehiculoTimer.current = setTimeout(async () => {
            try {
                const query = `description=${encodeURIComponent(description)}&model=${modelo}&company=${AUTO_SEARCH_COMPANY}&pickup=0`
                const result: ModelAutoResponse[] = await (await APIInvoke.invokeGET(`/autos/buscador/modelos?${query}`)).json()
                setVehiculoOptions(Array.isArray(result) ? result : [])
            } catch (error) {
                console.error(error)
                setVehiculoOptions([])
            } finally {
                setVehiculoSearching(false)
            }
        }, AUTO_SEARCH_DELAY_MS)
    }

    const libreDescripcion = (text: string): VehiculoOption | null =>
        (text.trim() ? { descripcion: text.trim(), marca: '', tipo: '', amis: '', libre: true } : null)

    /** Como en la captura anterior: el texto tecleado aparece como primera opción para usarlo tal cual. */
    const typedVehiculo = vehiculoInput.trim()
    const libreOption = libreDescripcion(vehiculoInput)
    const vehiculoChoices: VehiculoOption[] = libreOption && !vehiculoOptions.some((o) => vehiculoLabel(o) === typedVehiculo)
        ? [libreOption, ...vehiculoOptions]
        : vehiculoOptions

    const vehiculoInputChange = (value: string, reason: string) => {
        setVehiculoInput(value)
        if (reason === 'clear') {
            patchVehiculo({ descripcion: null })
            setVehiculoOptions([])
            return
        }
        if (reason === 'input') searchVehiculos(value)
    }

    /** Si se escribió y no se eligió opción, al salir del campo se queda el texto libre. */
    const commitVehiculoInput = () => {
        if (vehiculoInput.trim() === vehiculoLabel(state.vehiculo.descripcion).trim()) return
        const libre = libreDescripcion(vehiculoInput)
        patchVehiculo({ descripcion: libre ? { descripcion: libre.descripcion, marca: '', tipo: '', amis: '' } : null })
    }

    const pickVehiculo = (value: VehiculoOption | string | null) => {
        const option = typeof value === 'string' ? libreDescripcion(value) : value
        if (!option) {
            patchVehiculo({ descripcion: null })
            return
        }
        const { descripcion, marca, tipo, amis } = option
        patchVehiculo({ descripcion: { descripcion, marca, tipo, amis } })
    }

    /** Cambiar primas recalcula el IVA (salvo vida) y conserva el % de comisión, como la versión anterior. */
    const changePrima = (field: 'neta' | 'derecho' | 'financiamiento', value: string) => {
        patchG({ [field]: sanitizeDecimal(value) })
        const keepPct = field === 'neta' && state.comisionMode === 'monto'
        patch({
            ivaEdit: false,
            ...(keepPct ? { comisionMode: 'pct' as ComisionMode, pctEdit: true } : {}),
        })
        if (keepPct) patchG({ pct: String(Number(pctN.toFixed(6))), comisionMonto: '' })
    }

    const change = {
        ramo: (value: string) => patch({ ramo: value as RamoKey | '' }),
        contratante: setContratante,
        aseguradora: (value: string | number) => patchG({ aseguradora: toId(value), agente: '' }),
        agente: (value: string | number) => {
            patchG({ agente: toId(value) })
            patch({ pctEdit: false, comisionMode: 'pct' })
        },
        numero: (value: string) => patchG({ numero: value.toUpperCase() }),
        plan: (value: string) => patchG({ plan: value }),
        concepto: (value: string) => patchG({ concepto: value }),
        /** Como antes: cambiar el inicio siempre mueve el fin a un año después, también al editar. */
        desde: (value: string) => {
            patchG({ desde: value, hasta: addOneYear(value) })
            patch({ finEdit: false })
        },
        hasta: (value: string) => {
            patchG({ hasta: value })
            patch({ finEdit: true })
        },
        formaPago: (value: string) => patchG({ formaPago: value as FormaPago }),
        moneda: (value: string | number) => patchG({ moneda: toId(value) }),
        vendedor: (value: string | number) => {
            const vendedor = catalogs.vendedores.find((item) => sameId(item.id, value))
            patchG({ vendedor: toId(value), vendedorEntity: vendedor || null })
        },
        conducto: (value: string) => patchG({ conducto: value as Conducto | '' }),
        neta: (value: string) => changePrima('neta', value),
        derecho: (value: string) => changePrima('derecho', value),
        financiamiento: (value: string) => changePrima('financiamiento', value),
        iva: (value: string) => {
            patchG({ iva: sanitizeDecimal(value) })
            patch({ ivaEdit: true })
        },
        pct: (value: string) => {
            const clean = sanitizeDecimal(value, 2, 3)
            patchG({ pct: num(clean) > PCT_MAX ? String(PCT_MAX) : clean, comisionMonto: '' })
            patch({ pctEdit: true, comisionMode: 'pct' })
        },
        /** La comisión no puede superar la prima neta (equivale a más de 100%). */
        comisionMonto: (value: string) => {
            const clean = sanitizeDecimal(value)
            patchG({ comisionMonto: neta > 0 && num(clean) > neta ? formatAmount(neta) : clean })
            patch({ comisionMode: 'monto' })
        },
        /** Al salir del campo deja el monto como `###,##0.00`; vacío se queda vacío. */
        amountBlur: (field: AmountField) => {
            if (field === 'iva' && !ivaManual) return
            if (field === 'comisionMonto' && !porMonto) return
            const raw = state.generales[field]
            patchG({ [field]: raw.trim() ? formatAmount(num(raw)) : '' })
        },
        pctBlur: () => {
            if (porMonto || !state.generales.pct.trim()) return
            patchG({ pct: formatPct(num(state.generales.pct)) })
        },
        clienteEsAsegurado: toggleClienteEsAsegurado,
        asegurado: changeAsegurado,
        vehiculoModelo: (value: string | number) => patchVehiculo({ modelo: Number(value) }),
        vehiculoDescripcion: pickVehiculo,
        vehiculoInput: vehiculoInputChange,
        vehiculoBlur: commitVehiculoInput,
        vehiculoNoSerie: (value: string) => patchVehiculo({ noSerie: clip(value, NO_SERIE_MAX).toUpperCase() }),
        vehiculoPlacas: (value: string) => patchVehiculo({ placas: clip(value, PLACAS_MAX).toUpperCase() }),
        texto: (value: string) => patch({ texto: value }),
        prorrDer: () => patch({ prorrDer: !state.prorrDer }),
        prorrFin: () => patch({ prorrFin: !state.prorrFin }),
    }

    // CustomerForm recarga cuando cambia `cliente`; se memoriza para no reiniciar el diálogo abierto.
    const clientDialogCliente = useMemo(() => clientePrefill(state.generales.contratante), [state.generales.contratante])
    const conductoOptions = catalogs.conductos.length ? catalogs.conductos : [DEFAULT_CONDUCTO]
    const revCount = Object.values(state.rev).filter(Boolean).length

    return {
        state,
        catalogs,
        loading,
        message,
        backUrl,
        ramoMeta,
        aseguradoraRow,
        claves,
        effClave,
        claveAuto,
        hasta,
        finAuto,
        pctAuto,
        pctN,
        pctValue,
        comisionValue,
        neta,
        derecho,
        financiamiento,
        iva,
        ivaValue,
        ivaHint,
        total,
        comision,
        forma,
        allErr,
        se,
        subs,
        sumRec,
        recDiff,
        errList,
        steps,
        revCount,
        conductoOptions,
        resumenNumero: state.generales.numero || 'Póliza nueva',
        clientOptions,
        clientSearching,
        clientDialog,
        claveDialog,
        resetDialog,
        okSec,
        errorCount,
        change,
        searchClientes,
        uploadPdf,
        previewWithClave,
        closeClaveDialog,
        calcularRecibos,
        stepNext,
        guardar,
        changeRecibo,
        reciboValue,
        blurRecibo: () => setReciboDraft(null),
        togglePagado,
        reiniciar,
        goToStep: (i: number) => { if (i <= state.maxStep) patch({ step: i }) },
        stepBack: () => patch({ step: state.step - 1 }),
        addAsegurado: () => patch({ asegurados: [...state.asegurados, emptyAsegurado()] }),
        removeAsegurado: (key: number) => patch({ asegurados: state.asegurados.filter((a, index) => index === 0 || a.key !== key) }),
        parentescoOptions,
        vehiculoChoices,
        vehiculoInput,
        vehiculoSearching,
        clienteIncompletoDialog,
        closeClienteIncompletoDialog: () => setClienteIncompletoDialog(false),
        /** El check de "contratante es asegurado" solo aplica al capturar (como en la versión anterior). */
        canUseContratante: !state.polizaId,
        /** Igual que antes: con contratante elegido el botón lo edita; sin él, da de alta uno nuevo. */
        clientDialogCliente,
        contratanteIncompleto: Boolean(state.generales.contratante?.esRobot),
        openClientDialog: () => setClientDialog(true),
        closeClientDialog: () => setClientDialog(false),
        clientCreated: (cliente?: ClienteDTO | null) => {
            setClientDialog(false)
            if (!cliente) return
            setContratante(cliente)
            setClientOptions((opts) => [cliente, ...opts.filter((o) => o.id !== cliente.id)])
        },
        /** Al editar se bloquea lo que el PUT no soporta o sobrescribiría la póliza: forma de pago, carátula, reinicio y recálculo de recibos. */
        isEditing: Boolean(state.polizaId),
        isRenewing: Boolean(state.polizaIDRenovo && !state.polizaId),
        canSave: state.step === LAST_STEP,
        expandedRecibo,
        toggleRecibo: (index: number) => setExpandedRecibo((current) => (current === index ? null : index)),
        changeReciboDate,
        changeFolioPago,
        applyAllIndex,
        askApplyToAll: (index: number) => setApplyAllIndex(index),
        cancelApplyToAll: () => setApplyAllIndex(null),
        applyToAll,
        adjuntosOpen,
        openAdjuntos: () => setAdjuntosOpen(true),
        closeAdjuntos: () => setAdjuntosOpen(false),
        openResetDialog: () => setResetDialog(true),
        closeResetDialog: () => setResetDialog(false),
    }
}
